import { Fragment, useState } from "react";
import type React from "react";
import { Box, Group, Text } from "@mantine/core";
import { Button, Table } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type {
  RowEdit,
} from "@models/job";
import { MappingResultKind } from "@models/mappingResult";
import { TM_COLUMNS, colSpanOf } from "@utils/resultTableColumns";
import type {
  MappingResultRow,
  MappingResultRowTm,
  RowAlternatives,
  TmTarget,
} from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";
import TmJobResultsTableRow from "./TmJobResultsTableRow";
import TmJobResultsExpandedRow from "./TmJobResultsExpandedRow";
import TmAddRow from "./TmAddRow";
import formatVariantLabel from "@utils/variantLabel";

interface TmJobResultsTableProps {
  rows: MappingResultRowTm[];
  edits: Map<number, RowEdit>;
  saveStates: Map<number, RowSaveStatus>;
  jobId: string;
  registerRowRef: (rowIndex: number, el: HTMLTableRowElement | null) => void;
  onAccept: (rowIndex: number, text?: string) => void;
  onReject: (rowIndex: number) => void;
  onPickAlternative: (rowIndex: number, rank: number) => void;
  onRecover: (rowIndex: number) => void;
  onRetrySave: (rowIndex: number) => void;
  onRowRestored: (row: MappingResultRow) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Lazily fetch a row's alternatives the first time its dropdown opens. */
  onLoadAlternatives: (rowIndex: number, kind: MappingResultKind) => void;
  /** Row indexes whose alternatives are currently being fetched. */
  alternativesLoading: Set<number>;
  /** The candidate cache, keyed by row index — see `useJobResults`. */
  alternatives: Map<number, RowAlternatives>;
  onAddRow: (
    itemMpId: number,
    itemMpText: string,
    value: string,
  ) => Promise<void>;
}

// Derived, never retyped: a column added to TM_COLUMNS without a matching
// bump here used to misalign the expanded row, and nothing failed on it.
/** The vehicle variant a row was extracted under — TM only; OE rows have no
 *  variant axis. Rows are grouped under a header whenever it changes. */

const variantOf = (row: MappingResultRow): string | null =>
  row.kind === MappingResultKind.Tm ? row.source.variant : null;

const COL_SPAN = colSpanOf(TM_COLUMNS);

const TmJobResultsTable: React.FC<TmJobResultsTableProps> = ({
  rows,
  edits,
  saveStates,
  jobId,
  registerRowRef,
  onAccept,
  onReject,
  onPickAlternative,
  onRecover,
  onRetrySave,
  onRowRestored,
  hasMore,
  loadingMore,
  onLoadMore,
  onLoadAlternatives,
  alternativesLoading,
  alternatives,
  onAddRow,
}) => {
  const { t } = useTranslation();
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

  const toggleExpand = (rowIndex: number): void => {
    const willExpand = expandedRowIndex !== rowIndex;
    setExpandedRowIndex(willExpand ? rowIndex : null);
    // Fetch the alternatives on first expand (cached + de-duped in the hook).
    // The kind is this table's, stated rather than looked up: the response
    // carries no discriminator and this table renders exactly one shape.
    if (willExpand) onLoadAlternatives(rowIndex, MappingResultKind.Tm);
  };

  /** One row's candidates once its dropdown has been opened — the cache is
   *  keyed by row index and empty until then. */
  const candidatesFor = (rowIndex: number): readonly TmTarget[] | undefined => {
    const loaded = alternatives?.get(rowIndex);
    return loaded?.kind === MappingResultKind.Tm ? loaded.alternatives : undefined;
  };

  return (
    <Box className="job-results-page__table-wrap job-results-page__table-wrap--tm">
      <Table
        striped
        withTableBorder
        withColumnBorders
        verticalSpacing="sm"
        horizontalSpacing="sm"
        layout="fixed"
        className="job-results-page__table job-results-page__table--tm"
      >
        <colgroup>
          <col style={{ width: "3%" }} /> {/* # */}
          <col style={{ width: "14%" }} /> {/* Path */}
          <col style={{ width: "21%" }} /> {/* Value */}
          <col style={{ width: "25%" }} /> {/* Item-MP Listing */}
          <col style={{ width: "5%" }} /> {/* ItemMP ID */}
          <col style={{ width: "12%" }} /> {/* value */}
          <col style={{ width: "10%" }} /> {/* Status — reviewer axis only */}
          <col style={{ width: "7%" }} /> {/* Actions */}
          <col style={{ width: "3%" }} /> {/* Alternatives */}
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="job-results-page__tm-index-head">
              <Text size="xs" c="dimmed" fw={600}>
                {t("jobResultsPage.table.rowIndex", { defaultValue: "#" })}
              </Text>
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.sourcePath", {
                defaultValue: "Path",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.sourceValue", {
                defaultValue: "Value",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.itemMpListing", {
                defaultValue: "Item-MP Listing",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.itemMpId", {
                defaultValue: "ItemMP ID",
              })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.tmTable.value", { defaultValue: "value" })}
            </Table.Th>
            <Table.Th className="job-results-page__tm-status-head">
              {t("jobResultsPage.table.status", { defaultValue: "Status" })}
            </Table.Th>
            <Table.Th>
              {t("jobResultsPage.table.actions", { defaultValue: "Actions" })}
            </Table.Th>
            <Table.Th aria-label={t("jobResultsPage.actions.viewAlternatives", {
              defaultValue: "Alternatives",
            })} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, idx) => {
            const edit = edits.get(row.rowIndex);
            const expanded = expandedRowIndex === row.rowIndex;
            const variant = variantOf(row);
            const prevVariant = idx > 0 ? variantOf(rows[idx - 1]) : null;
            const showVariantHeader =
              variant !== null && variant !== prevVariant;
            const saveState = saveStates.get(row.rowIndex);
            const saving = saveState?.state === "saving";
            return (
              <Fragment key={row.rowIndex}>
                {showVariantHeader && (
                  <Table.Tr className="job-results-page__variant-row">
                    <Table.Td colSpan={COL_SPAN}>
                      <Text className="job-results-page__variant-label">
                        {t("jobResultsPage.table.variant", {
                          defaultValue: "Variant",
                        })}
                        {" · "}
                        {formatVariantLabel(variant)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                <TmJobResultsTableRow
                candidates={candidatesFor(row.rowIndex)}
                  row={row}
                  edit={edit}
                  saveState={saveState}
                  jobId={jobId}
                  expanded={expanded}
                  registerRef={(el) => registerRowRef(row.rowIndex, el)}
                  onToggleExpand={() => toggleExpand(row.rowIndex)}
                  onAccept={(text) => onAccept(row.rowIndex, text)}
                  onReject={() => onReject(row.rowIndex)}
                  onRecover={() => onRecover(row.rowIndex)}
                  onRetrySave={() => onRetrySave(row.rowIndex)}
                  onRowRestored={onRowRestored}
                />
                {expanded && (
                  <TmJobResultsExpandedRow
                    row={row}
                    edit={edit}
                    candidates={candidatesFor(row.rowIndex)}
                    colSpan={COL_SPAN}
                    disabled={saving}
                    loading={alternativesLoading.has(row.rowIndex)}
                    onPickAlternative={(rank) =>
                      onPickAlternative(row.rowIndex, rank)
                    }
                  />
                )}
              </Fragment>
            );
          })}
          <TmAddRow colSpan={COL_SPAN} jobId={jobId} onAddRow={onAddRow} />
        </Table.Tbody>
      </Table>

      {hasMore && (
        <Group justify="center" p="sm">
          <Button
            variant="subtle"
            size="sm"
            loading={loadingMore}
            onClick={onLoadMore}
          >
            {t("mappingServicePage.main.loadMore")}
          </Button>
        </Group>
      )}
    </Box>
  );
};

export default TmJobResultsTable;
